import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Stack,
  Box,
  Typography,
  Card,
  Divider,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Menu,
  ListItemIcon,
  ListItemText,
  Autocomplete,
  TextField,
  Chip,
  CircularProgress,
  IconButton
} from '@mui/material';
import {
  PersonOutlined as PersonIcon,
  GroupsOutlined as GroupsIcon,
  GroupOutlined as GroupIcon,
  SearchOutlined as SearchIcon,
  CloseOutlined as CloseIcon,
  RemoveOutlined as RemoveIcon,
  VisibilityOutlined as VisibilityLevelIcon,
  EditOutlined as EditLevelIcon,
  AdminPanelSettingsOutlined as AdminLevelIcon
} from '@mui/icons-material';
import { listCompanyUsers, listTeams, listGroups } from '../../../services/api';
import { useAuth } from '../../../contexts/AuthContext';

const sectionHeaderProps = {
  component: 'div',
  variant: 'caption',
  color: 'text.primary',
  sx: { fontWeight: 600, fontSize: '0.875rem', display: 'block', mb: 1 }
};

/** Outlined level icons for this dialog (parents may pass filled or mixed icons). */
const getMinimalLevelIcon = (roleKey) => {
  const sx = { fontSize: 22 };
  switch (roleKey) {
    case 'view-only':
      return <VisibilityLevelIcon sx={sx} />;
    case 'interact':
      return <PersonIcon sx={sx} />;
    case 'edit':
      return <EditLevelIcon sx={sx} />;
    case 'owner':
      return <AdminLevelIcon sx={sx} />;
    default:
      return null;
  }
};

const CustomPermissionsDialog = ({
  open,
  onClose,
  onSave,
  accessList = [],
  permissionLevels,
  title = 'Add Custom Permissions',
  creatorId = null, // ID of the entity creator to exclude from user list
  canEdit = true
}) => {
  const { user } = useAuth();
  const [accessRole, setAccessRole] = useState('interact');
  const [accessType, setAccessType] = useState('user');
  const [selectedEntity, setSelectedEntity] = useState(null);

  // Pending items grouped by permission level: { 'owner': [...items], 'interact': [...items] }
  const [pendingItems, setPendingItems] = useState({});
  const [draftAccessList, setDraftAccessList] = useState([]);

  // Role menu for "Who has access" chip
  const [roleMenuAnchor, setRoleMenuAnchor] = useState(null);
  const [editingAccessId, setEditingAccessId] = useState(null);

  // Real data for search functionality
  const [availableUsers, setAvailableUsers] = useState([]);
  const [availableTeams, setAvailableTeams] = useState([]);
  const [availableGroups, setAvailableGroups] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(false);
  const [loadingTeams, setLoadingTeams] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);

  // Load users when dialog opens and access type is user
  useEffect(() => {
    const loadUsers = async () => {
      if (!open || accessType !== 'user' || !user?.companyId) return;
      
      if (availableUsers.length === 0 && !loadingUsers) {
        setLoadingUsers(true);
        try {
          const response = await listCompanyUsers(user.companyId);
          const users = response.users || [];
          // Filter out the creator - they should not be in custom permissions list
          const filteredUsers = creatorId ? users.filter(u => u.id !== creatorId) : users;
          const formattedUsers = filteredUsers.map(u => ({
            id: u.id,
            name: u.name || `${u.firstName || ''} ${u.lastName || ''}`.trim() || u.email,
            email: u.email,
            role: u.role || 'USER',
            teamName: u.teamName || null
          }));
          setAvailableUsers(formattedUsers);
        } catch (error) {
          console.error('Failed to load users:', error);
        } finally {
          setLoadingUsers(false);
        }
      }
    };
    loadUsers();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accessType, user?.companyId]);

  // Load teams when dialog opens and access type is team
  useEffect(() => {
    const loadTeams = async () => {
      if (!open || accessType !== 'team' || !user?.companyId) return;
      
      if (availableTeams.length === 0 && !loadingTeams) {
        setLoadingTeams(true);
        try {
          const response = await listTeams(user.companyId);
          const teams = response.teams || [];
          const formattedTeams = teams.map(t => ({
            id: t.id,
            name: t.name,
            description: t.description || '',
            memberCount: t.memberCount || 0
          }));
          setAvailableTeams(formattedTeams);
        } catch (error) {
          console.error('Failed to load teams:', error);
        } finally {
          setLoadingTeams(false);
        }
      }
    };
    loadTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accessType, user?.companyId]);

  // Load groups when dialog opens and access type is group
  useEffect(() => {
    const loadGroups = async () => {
      if (!open || accessType !== 'group' || !user?.companyId) return;
      
      if (availableGroups.length === 0 && !loadingGroups) {
        setLoadingGroups(true);
        try {
          const response = await listGroups(user.companyId);
          const groups = response.groups || [];
          const formattedGroups = groups.map(g => ({
            id: g.id,
            name: g.name,
            description: g.description || '',
            memberCount: g.memberCount || 0
          }));
          setAvailableGroups(formattedGroups);
        } catch (error) {
          console.error('Failed to load groups:', error);
        } finally {
          setLoadingGroups(false);
        }
      }
    };
    loadGroups();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, accessType, user?.companyId]);

  // Keep access changes local until the user explicitly applies them.
  useEffect(() => {
    if (open) {
      setDraftAccessList(accessList);
    }
    if (!open) {
      setAccessRole('interact');
      setAccessType('user');
      setSelectedEntity(null);
      setPendingItems({});
      setRoleMenuAnchor(null);
      setEditingAccessId(null);
      setDraftAccessList([]);
    }
  }, [open, accessList]);

  // Clear selected entity when access type changes
  useEffect(() => {
    setSelectedEntity(null);
  }, [accessType]);

  // Auto-add items to pending list when selected
  const handleEntitySelect = (event, newValue) => {
    if (!newValue) {
      setSelectedEntity(null);
      return;
    }
    
    // Handle single selection (non-multiple autocomplete)
    const item = newValue;
    
    // Check if already in pending list
    const allPendingIds = Object.values(pendingItems).flat().map(pendingItem => pendingItem.id);
    const existingAccessIds = draftAccessList.map((access) => access.id);
    if (allPendingIds.includes(item.id) || existingAccessIds.includes(item.id)) {
      setSelectedEntity(null); // Clear selection even if duplicate
      return; // Don't add duplicates
    }

    let newItem;
    if (accessType === 'user') {
      newItem = {
        id: item.id,
        type: 'user',
        name: item.name,
        role: accessRole,
        email: item.email,
        description: item.role,
        teamName: item.teamName
      };
    } else if (accessType === 'team') {
      newItem = {
        id: item.id,
        type: 'team',
        name: item.name,
        role: accessRole,
        description: item.description,
        memberCount: item.memberCount
      };
    } else if (accessType === 'group') {
      newItem = {
        id: item.id,
        type: 'group',
        name: item.name,
        role: accessRole,
        description: item.description,
        memberCount: item.memberCount
      };
    }

    if (newItem) {
      setPendingItems(prev => ({
        ...prev,
        [accessRole]: [...(prev[accessRole] || []), newItem]
      }));
      
      // Clear selection so user can select another
      setSelectedEntity(null);
    }
  };

  // Remove item from pending list
  const handleRemovePending = (role, itemId) => {
    setPendingItems(prev => ({
      ...prev,
      [role]: prev[role]?.filter(item => item.id !== itemId) || []
    }));
  };

  const hasPendingItems = Object.values(pendingItems).some(items => items.length > 0);
  const totalPendingCount = Object.values(pendingItems).reduce((sum, items) => sum + items.length, 0);
  const hasAccessChanges = JSON.stringify(draftAccessList) !== JSON.stringify(accessList);
  const hasUnsavedChanges = hasPendingItems || hasAccessChanges;

  const handleUpdateAccess = () => {
    if (!hasUnsavedChanges) {
      onClose();
      return;
    }
    onSave?.([
      ...draftAccessList,
      ...Object.values(pendingItems).flat(),
    ]);
    setPendingItems({});
    onClose();
  };

  const getTypeIcon = (type) => {
    const sx = { fontSize: '1rem', color: 'text.secondary' };
    switch (type) {
      case 'user':
        return <PersonIcon sx={sx} />;
      case 'team':
        return <GroupsIcon sx={sx} />;
      case 'group':
        return <GroupIcon sx={sx} />;
      default:
        return null;
    }
  };

  const filteredAccessList = draftAccessList.filter(
    access => !(access.type === 'user' && creatorId && access.id === creatorId)
  );

  // Filter out already pending items and creator from available options
  const getAvailableOptions = () => {
    const allPendingIds = Object.values(pendingItems).flat().map(item => item.id);
    const existingAccessIds = draftAccessList.map((access) => access.id);
    
    switch (accessType) {
      case 'user':
        // Filter out pending items and creator
        return availableUsers.filter(u => 
          !allPendingIds.includes(u.id) && !existingAccessIds.includes(u.id) &&
          (!creatorId || u.id !== creatorId)
        );
      case 'team':
        return availableTeams.filter(t => !allPendingIds.includes(t.id) && !existingAccessIds.includes(t.id));
      case 'group':
        return availableGroups.filter(g => !allPendingIds.includes(g.id) && !existingAccessIds.includes(g.id));
      default:
        return [];
    }
  };

  return (
    <Dialog 
      open={open} 
      onClose={onClose}
      maxWidth="md"
      fullWidth
    >
      <DialogTitle sx={{ typography: 'subtitle2', fontWeight: 600, color: 'text.primary', pb: 1 }}>
        {title}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={3} sx={{ mt: 1 }}>
          {/* Permission Level Selection */}
          <Box>
            <Typography {...sectionHeaderProps}>
              Permission Level
            </Typography>
            <Stack direction="row" spacing={2}>
              {Object.entries(permissionLevels).map(([key, level]) => (
                <Card 
                  key={key}
                  variant={accessRole === key ? "elevated" : "outlined"}
                  sx={{ 
                    p: 2,
                    flex: 1, // Make cards equal width
                    cursor: 'pointer',
                    border: accessRole === key ? 2 : 1,
                    borderColor: accessRole === key ? 'primary.main' : 'divider',
                    bgcolor: accessRole === key ? 'primary.50' : 'background.paper'
                  }}
                  onClick={() => setAccessRole(key)}
                >
                  <Stack spacing={1} alignItems="center">
                    <Box sx={{ color: accessRole === key ? 'primary.main' : 'text.secondary', display: 'flex' }}>
                      {getMinimalLevelIcon(key) ?? level.icon}
                    </Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={accessRole === key ? 600 : 500}
                      color="text.primary"
                    >
                      {level.label}
                    </Typography>
                    <Typography variant="caption" color="text.secondary" textAlign="center">
                      {level.description}
                    </Typography>
                  </Stack>
                </Card>
              ))}
            </Stack>
          </Box>

          <Divider />

          {/* Entity Selection */}
          <Box>
            <Typography {...sectionHeaderProps}>
              Select {accessType === 'user' ? 'users' : accessType === 'team' ? 'teams' : 'groups'}
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Selected items will automatically be added to the pending list with the selected permission level
            </Typography>
            
            <FormControl
              fullWidth
              sx={{
                mb: 2,
                '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: 'text.primary' }
              }}
            >
              <InputLabel>Entity Type</InputLabel>
              <Select
                value={accessType}
                onChange={(e) => setAccessType(e.target.value)}
                label="Entity Type"
              >
                <MenuItem value="user">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    Users
                  </Box>
                </MenuItem>
                <MenuItem value="team">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupsIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    Teams
                  </Box>
                </MenuItem>
                <MenuItem value="group">
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <GroupIcon sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    Groups
                  </Box>
                </MenuItem>
              </Select>
            </FormControl>

            {/* Single Search Bar - changes based on accessType */}
            {accessType === 'user' && (
              <Autocomplete
                options={getAvailableOptions()}
                loading={loadingUsers}
                value={selectedEntity}
                onChange={handleEntitySelect}
                getOptionLabel={(option) => option.teamName ? `${option.name} (${option.teamName})` : option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select users"
                    placeholder="Type to search users..."
                    sx={{ '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: 'text.primary' } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />,
                      endAdornment: (
                        <>
                          {loadingUsers ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.teamName || 'No team'} • {option.email} • {option.role}
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}

            {accessType === 'team' && (
              <Autocomplete
                options={getAvailableOptions()}
                loading={loadingTeams}
                value={selectedEntity}
                onChange={handleEntitySelect}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select teams"
                    placeholder="Type to search teams..."
                    sx={{ '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: 'text.primary' } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />,
                      endAdornment: (
                        <>
                          {loadingTeams ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description} • {option.memberCount} members
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}

            {accessType === 'group' && (
              <Autocomplete
                options={getAvailableOptions()}
                loading={loadingGroups}
                value={selectedEntity}
                onChange={handleEntitySelect}
                getOptionLabel={(option) => option.name}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Search and select groups"
                    placeholder="Type to search groups..."
                    sx={{ '& .MuiInputLabel-root, & .MuiInputLabel-root.Mui-focused': { color: 'text.primary' } }}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary', fontSize: '1rem' }} />,
                      endAdornment: (
                        <>
                          {loadingGroups ? <CircularProgress color="inherit" size={20} /> : null}
                          {params.InputProps.endAdornment}
                        </>
                      )
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Box>
                      <Typography variant="body2" fontWeight="medium">
                        {option.name}
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        {option.description} • {option.memberCount} members
                      </Typography>
                    </Box>
                  </Box>
                )}
              />
            )}
          </Box>

          {/* Pending Items by Permission Level */}
          {hasPendingItems && (
            <>
              <Divider />
              <Box>
                <Typography {...sectionHeaderProps}>
                  Pending Items ({totalPendingCount})
                </Typography>
                {Object.entries(pendingItems).map(([role, items]) => {
                  if (!items || items.length === 0) return null;
                  
                  return (
                    <Box key={role} sx={{ mb: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ fontWeight: 600, fontSize: '0.875rem', display: 'block', mb: 1 }}
                      >
                        {permissionLevels[role]?.label || role} ({items.length})
                      </Typography>
                      <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap>
                        {items.map(item => (
                          <Chip
                            key={`${role}-${item.id}`}
                            label={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                {getTypeIcon(item.type)}
                                <span>{item.name}</span>
                              </Box>
                            }
                            onDelete={() => handleRemovePending(role, item.id)}
                            deleteIcon={<CloseIcon />}
                            variant="outlined"
                            sx={{ mb: 1 }}
                          />
                        ))}
                      </Stack>
                    </Box>
                  );
                })}
              </Box>
            </>
          )}

          {/* Who has access - lower profile compact list */}
          {filteredAccessList.length > 0 && (
            <>
              <Divider />
              <Box>
                <Typography {...sectionHeaderProps} sx={{ ...(sectionHeaderProps.sx || {}), mb: 1 }}>
                  Who has access
                </Typography>
                <Stack spacing={0.5}>
                  {filteredAccessList.map((access) => (
                    <Box
                      key={access.id}
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1.5,
                        py: 0.75,
                        px: 1,
                        borderRadius: 1,
                        '&:hover': { bgcolor: 'action.hover' }
                      }}
                    >
                      <Box sx={{ color: 'text.secondary', display: 'flex', alignItems: 'center' }}>
                        {getTypeIcon(access.type)}
                      </Box>
                      <Typography variant="body2" sx={{ flex: 1, minWidth: 0 }} noWrap>
                        {access.name}
                      </Typography>
                      <Chip
                        label={permissionLevels?.[access.role]?.label || access.role}
                        size="small"
                        variant="outlined"
                        onClick={canEdit ? (e) => {
                          e.stopPropagation();
                          setRoleMenuAnchor(e.currentTarget);
                          setEditingAccessId(access.id);
                        } : undefined}
                        sx={{
                          fontSize: '0.75rem',
                          height: 22,
                          cursor: canEdit ? 'pointer' : 'default'
                        }}
                      />
                      {canEdit && (
                        <IconButton
                          size="small"
                          onClick={() => setDraftAccessList((current) => current.filter((item) => item.id !== access.id))}
                          sx={{ color: 'text.secondary' }}
                          aria-label="Remove access"
                        >
                          <RemoveIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Box>
                  ))}
                </Stack>
              </Box>
            </>
          )}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
        <Button 
          onClick={handleUpdateAccess}
          variant="contained"
          disabled={!hasUnsavedChanges}
        >
          Update access{totalPendingCount ? ` (${totalPendingCount})` : ''}
        </Button>
      </DialogActions>

      {/* Role change menu for "Who has access" chips */}
      <Menu
        anchorEl={roleMenuAnchor}
        open={Boolean(roleMenuAnchor)}
        onClose={() => {
          setRoleMenuAnchor(null);
          setEditingAccessId(null);
        }}
      >
        {permissionLevels && Object.entries(permissionLevels).map(([key, level]) => {
          const currentAccess = filteredAccessList.find(a => a.id === editingAccessId);
          const isSelected = currentAccess?.role === key;
          return (
            <MenuItem
              key={key}
              selected={isSelected}
              onClick={() => {
                setDraftAccessList((current) => current.map((access) => (
                  access.id === editingAccessId ? { ...access, role: key } : access
                )));
                setRoleMenuAnchor(null);
                setEditingAccessId(null);
              }}
            >
              <ListItemIcon sx={{ color: 'text.secondary', minWidth: 36 }}>
                {getMinimalLevelIcon(key) ?? level.icon}
              </ListItemIcon>
              <ListItemText
                primary={level.label}
                secondary={level.description}
                primaryTypographyProps={{ variant: 'body2', fontWeight: 500, color: 'text.primary' }}
                secondaryTypographyProps={{ variant: 'caption', color: 'text.secondary' }}
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Dialog>
  );
};

export default CustomPermissionsDialog;
